import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { createProxySignal, date } from 'common';
import { Dorito, requireAttendanceGridContext, ToolButton } from '../../..';
import MonthGrid from './MonthGrid';
import styles from '../st-attendance-grid.module.css';
import { CalendarService } from '../services/CalendarService';

const { startOfWeek, addDays, addMonths, startOfMonth, endOfMonth, endOfWeek, truncateTime } = date;

export function MiniNavigation() {
    const ctx = requireAttendanceGridContext();

    return <div class={styles['mini-calendar-nav-container']}>
        <MonthGrid />
    </div>
}

export function MiniCalendar() {
    const ctx = requireAttendanceGridContext();
    const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return <div class={styles['mini-calendar-grid']}>
        <div class={styles['mini-calendar-day-headers']}>
            <For each={dayHeaders}>
                {(header) => (
                    <div class={styles['mini-calendar-day-header']}>{header}</div>
                )}
            </For>
        </div>
        <For each={ctx.weeks()}>{(w) => <Week date={w} />}</For>
        <SelectedDaysIndicator />
    </div>
}

function SelectedDaysIndicator() {
    const MS_PER_DAY = (1000 * 3600 * 24);
    const ctx = requireAttendanceGridContext();

    const [cellHeight, setCellHeight] = createSignal(30);
    const [cellWidth, setCellWidth] = createSignal(20);
    const [headerHeight, setHeaderHeight] = createSignal(0);

    function initElement(el: HTMLDivElement)
    {
        const observer = new ResizeObserver((entries) => {
            requestAnimationFrame(() => {
                if (el.parentElement) {
                    const dt = ctx.day();
                    const thisMonth = startOfWeek(startOfMonth(dt));
                    const nextMonth = endOfWeek(endOfMonth(dt));
                    const days = (nextMonth.getTime() - thisMonth.getTime()) / MS_PER_DAY;
                    const weeksInMonth = Math.ceil(days / 7);

                    // Get the header row height - find any header element
                    const headerCells = el.parentElement.querySelectorAll(`.${styles['mini-calendar-day-header']}`);
                    let measuredHeaderHeight = 0;
                    if (headerCells.length > 0) {
                        measuredHeaderHeight = headerCells[0].getBoundingClientRect().height;
                        setHeaderHeight(measuredHeaderHeight);
                    }

                    const rect = el.parentElement.getBoundingClientRect();
                    const parentPadding = 8; // Account for parent padding
                    const gap = 2; // Account for grid gap

                    // Calculate cell dimensions accounting for padding and gaps
                    const availableWidth = rect.width - (parentPadding * 2) - (gap * 6);
                    setCellWidth(Math.floor(availableWidth / 7));

                    const availableHeight = rect.height - measuredHeaderHeight - (parentPadding * 2) - (gap * (weeksInMonth - 1));
                    setCellHeight(Math.floor(availableHeight / weeksInMonth));
                }
            });
        });

        requestAnimationFrame(() => {
            if (el.parentElement) {
                observer.observe(el.parentElement);
            }
        });

        // Cleanup observer on component unmount
        onCleanup(() => {
            observer.disconnect();
        });
    }

    const top = createMemo(() => {
        const dt = ctx.day();
        const sow = startOfWeek(dt);
        const som = startOfWeek(startOfMonth(dt));

        // Calculate how many days from start of first week to start of selected week
        const daysBetween = (sow.getTime() - som.getTime()) / MS_PER_DAY;
        const weekNumber = Math.floor(daysBetween / 7);

        const parentPadding = 8;
        const gap = 2;
        const cellMargin = 2;

        // Position = header height + parent padding + (week number * cell height) + (week number * gap) + top margin of cell
        return headerHeight() + parentPadding + (weekNumber * cellHeight()) + (weekNumber * gap) + cellMargin;
    });

    const left = createMemo(() => {
        const dt = ctx.day();
        const sow = startOfWeek(dt);

        let dayOffset = 0;
        switch (ctx.displayMode()) {
            case 'Day':
                dayOffset = (dt.getTime() - sow.getTime()) / MS_PER_DAY;
                break;
            case 'WorkWeek':
                dayOffset = 1; // Start at Monday
                break;
            case 'Week':
                dayOffset = 0;
                break;
        }

        const parentPadding = 8;
        const gap = 2;
        const cellMargin = 2;

        // Position = parent padding + (day offset * cell width) + (day offset * gap) + cell left margin
        return parentPadding + (dayOffset * cellWidth()) + (dayOffset * gap) + cellMargin;
    });

    const width = createMemo(() => {
        const gap = 2;
        const cellMargin = 2;

        switch (ctx.displayMode()) {
            case 'Day':
                // Width of one cell minus left and right margins
                return cellWidth() - (cellMargin * 2);
            case 'Week':
                // Width of 7 cells + 6 gaps, minus left and right margins
                return (cellWidth() * 7) + (gap * 6) - (cellMargin * 2);
            case 'WorkWeek':
                // Width of 5 cells + 4 gaps, minus left and right margins
                return (cellWidth() * 5) + (gap * 4) - (cellMargin * 2);
        }
    });

    const height = createMemo(() => {
        const cellMargin = 2;
        // Height of cell minus top and bottom margins
        return cellHeight() - (cellMargin * 2);
    });

    return <Show when={ctx.displayMode() !== 'Month'}>
        <div ref={initElement} class={styles['mini-calendar-selected-indicator']} style={{
            top: `${top()}px`,
            left: `${left()}px`,
            width: `${width()}px`,
            height: `${height()}px`
        }}></div>
    </Show>
}

function Week(props: { date: Date; }) {
    const days = createMemo(() => {
        const dt = date.startOfWeek(props.date);
        return [0, 1, 2, 3, 4, 5, 6].map(i => date.addDays(dt, i));
    });

    return <>
        <For each={days()}>{(d) => <Day date={d} />}</For>
    </>;
}

function Day(props: { date: Date; }) {
    const ctx = requireAttendanceGridContext();

    const hasAttendance = createMemo(() => {
        // Check if any section has attendance data for this date
        const sections = ctx.attendance[0]();
        const dateStr = CalendarService.formatDateForApi(props.date);
        return sections.some(section =>
            section.studentSectionData?.some(stuData => stuData.date === dateStr)
        );
    });

    const inCurrentMonth = createMemo(() => {
        const currMonthDate = startOfMonth(ctx.day());
        const monthDate = startOfMonth(props.date);

        return currMonthDate.getFullYear() === monthDate.getFullYear()
            && currMonthDate.getMonth() === monthDate.getMonth();
    });

    const isToday = createMemo(() => truncateTime(props.date).getTime() === truncateTime(ctx.synergyToday[0]()).getTime());

    function navigate(ev: KeyboardEvent) {
        function moveTo(dt: Date) {
            if (ctx.dayEnabled(dt)) {
                ctx.setHoveredDay(dt);
            }
            ev.preventDefault();
        }

        switch (ev.key) {
            case ' ':
            case 'Enter':
                if (!ctx.isLoading[0]()) ctx.setDay(props.date);
                ev.preventDefault();
                break;
            case 'ArrowUp': return moveTo(addDays(props.date, -7));
            case 'ArrowDown': return moveTo(addDays(props.date, 7));
            case 'ArrowLeft': return moveTo(addDays(props.date, -1));
            case 'ArrowRight': return moveTo(addDays(props.date, 1));
        }
    }

    return <div onClick={() => { if (!ctx.isLoading[0]()) ctx.setDay(props.date); }}
        tabindex="0"
        onKeyDown={navigate}
        class={styles['mini-calendar-day']}
        style={{
            border: isToday() ? '2px solid #2196F3' : '1px solid transparent',
            cursor: ctx.dayEnabled(props.date) ? 'pointer' : 'default',
            'font-weight': isToday() ? '600' : '400',
            background: isToday() ? '#e3f2fd' : 'transparent',
            color: !inCurrentMonth() ? '#bbb' : (isToday() ? '#1976D2' : '#333'),
            opacity: ctx.dayEnabled(props.date) ? 1 : 0.4
        }}
        onMouseEnter={(e) => {
            if (ctx.dayEnabled(props.date)) {
                e.currentTarget.style.background = isToday() ? '#bbdefb' : '#f5f5f5';
                e.currentTarget.style.transform = 'scale(1.1)';
            }
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = isToday() ? '#e3f2fd' : 'transparent';
            e.currentTarget.style.transform = 'scale(1)';
        }}>
        <Show when={isToday()}>
            <div class={styles['mini-calendar-day-today-indicator']} />
        </Show>
        <div class={styles['mini-calendar-day-number']}>{props.date.getDate()}</div>
        <Show when={hasAttendance()}>
            <div class={styles['mini-calendar-day-attendance-indicator']} />
        </Show>
    </div>
}
